import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { processImage } from "../../services/aiImageProcessor";
import {
  getAIUsageInstructions,
  processPDFWithAI,
} from "../../services/aiPdfProcessor";

import {
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
} from "store/expense/expense.Api";
import { useGetInvoicesQuery } from "store/invoice/invoiceApi";
import {
  useCreateSupplierMutation,
  useGetEnabledSuppliersQuery,
} from "store/supplier/supplier.api";

import CreateSupplierComponent from "@components/CreateSupplierComponent/CreateSupplierComponent";
import { useCreateInvoiceHook } from "@hooks/CreateInvoiceHook/useCreateInvoiceHook";
import TableProducts from "./TableProducts";

import "./index.scss";

const CreateExpenseComponent = ({ expense, onClose, isUpdate }) => {
  const { t } = useTranslation();
  const [createExpense] = useCreateExpenseMutation();
  const [updateExpense] = useUpdateExpenseMutation();
  const [justificatifFile, setJustificatifFile] = useState(null);
  const { paymentInfo } = useCreateInvoiceHook();

  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [invoiceInputType, setInvoiceInputType] = useState("text"); // "text", "pdf", "image"
  const [importedPdfFile, setImportedPdfFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [aiStatus, setAiStatus] = useState(getAIUsageInstructions());
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [totalsUpdateTrigger, setTotalsUpdateTrigger] = useState(0);
  const [importedImageFile, setImportedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [supplierInput, setSupplierInput] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [foundSupplier, setFoundSupplier] = useState(null);
  const [showCreateSupplier, setShowCreateSupplier] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierToCreate, setSupplierToCreate] = useState(null);
  const [isCreatingSupplier, setIsCreatingSupplier] = useState(false);

  const {
    handleSubmit,
    register,
    control,
    watch,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      supplierId: "",
      date: "",
      invoiceNumber: "",
      description: "",
      justificatif: null,
      products: [
        {
          name: "",
          quantity: 1,
          priceHT: 0,
          tva: 20,
          description: "",
          remise: 0,
        },
      ],
    },
  });

  useEffect(() => {
    if (isUpdate && expense) {
      console.log("=== EXPENSE UPDATE INITIALIZATION ===");
      console.log("Expense object:", expense);
      console.log("Expense products:", expense.products);
      console.log("Products type:", typeof expense.products);
      console.log("Products length:", expense.products?.length);
      console.log("Is products array:", Array.isArray(expense.products));

      // Ensure products is an array and has at least one item
      let productsToUse = expense.products;

      if (!Array.isArray(productsToUse) || productsToUse.length === 0) {
        console.log("Products is not a valid array or empty, using default");
        productsToUse = [
          {
            name: "",
            quantity: 1,
            priceHT: 0,
            tva: 20,
            description: "",
            remise: 0,
          },
        ];
      } else {
        console.log("Using existing products:", productsToUse);
      }

      reset({
        supplierId: expense.supplierId?._id || "",
        date: expense.date?.substring(0, 10) || "",
        invoiceNumber: expense.invoiceNumber || "",
        description: expense.description || "",
        products: productsToUse,
      });

      // Initialize supplier input for editing
      if (expense.supplierId) {
        setSupplierInput(expense.supplierId.name);
        setFoundSupplier(expense.supplierId);
      }

      console.log("Form reset with products:", productsToUse);
    }
  }, [expense, isUpdate, reset]);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "products",
  });

  // Effect to replace field array when products are loaded for editing
  useEffect(() => {
    if (
      isUpdate &&
      expense &&
      expense.products &&
      Array.isArray(expense.products) &&
      expense.products.length > 0
    ) {
      console.log("=== REPLACING FIELD ARRAY ===");
      console.log("Replacing with products:", expense.products);
      replace(expense.products);
      console.log("Field array replaced");
    }
  }, [expense, isUpdate, replace]);

  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;
  const createdBy = currentUser?._id;

  const { data: suppliers } = useGetEnabledSuppliersQuery(companyId);
  const [createSupplier] = useCreateSupplierMutation();
  const { data: invoicesData, isLoading: isInvoicesLoading } =
    useGetInvoicesQuery(companyId);

  // Use useWatch for better performance and real-time updates
  const watchedProducts = useWatch({
    control,
    name: "products",
    defaultValue: [],
  });

  // Debug effect to monitor foundSupplier changes
  useEffect(() => {
    console.log("=== FOUND SUPPLIER CHANGED ===");
    console.log("foundSupplier:", foundSupplier);
    console.log("foundSupplier._id:", foundSupplier?._id);
    console.log("foundSupplier.id:", foundSupplier?.id);
  }, [foundSupplier]);

  // Debug effect to monitor fields changes
  useEffect(() => {
    console.log("=== FIELDS CHANGED ===");
    console.log("Fields length:", fields.length);
    console.log("Fields:", fields);
    console.log("Watched products:", watchedProducts);
  }, [fields, watchedProducts]);

  // Watch for changes in products to trigger totals update
  useEffect(() => {
    if (watchedProducts && watchedProducts.length > 0) {
      setForceUpdate((prev) => prev + 1);
      setTotalsUpdateTrigger((prev) => prev + 1);
    }
  }, [watchedProducts]);

  // Function to manually trigger totals update
  const triggerTotalsUpdate = useCallback(() => {
    setTotalsUpdateTrigger((prev) => prev + 1);
  }, []);

  // Handle supplier input change
  const handleSupplierInputChange = (e) => {
    const value = e.target.value || "";
    setSupplierInput(value);

    // Extract email from input
    const emailMatch = value.match(
      /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/
    );
    if (emailMatch) {
      const email = emailMatch[1];
      setSupplierEmail(email);

      // Check if supplier exists
      const existingSupplier = suppliers?.find(
        (s) => s.email?.toLowerCase() === email.toLowerCase()
      );

      if (existingSupplier) {
        console.log("Found existing supplier:", existingSupplier);
        console.log("Supplier _id:", existingSupplier._id);
        console.log("Supplier id:", existingSupplier.id);
        setFoundSupplier(existingSupplier);
        setValue("supplierId", existingSupplier._id);
        setShowCreateSupplier(false);
        toast.success(`Fournisseur trouvé: ${existingSupplier.name}`);
      } else {
        setFoundSupplier(null);
        setShowCreateSupplier(true);
      }
    } else {
      setSupplierEmail("");
      setFoundSupplier(null);
      setShowCreateSupplier(false);
    }
  };

  const handleCreateSupplier = () => {
    // Prepare supplier data for creation
    const supplierData = {
      name: supplierInput?.trim() || "",
      email: supplierEmail || null,
      address: null,
      phone: null,
    };

    setSupplierToCreate(supplierData);
    setShowSupplierModal(true);
  };

  const handleSupplierCreated = async (createdSupplier) => {
    try {
      setIsCreatingSupplier(true);

      console.log("=== SUPPLIER CREATED DEBUG ===");
      console.log("Raw created supplier response:", createdSupplier);
      console.log("Response type:", typeof createdSupplier);
      console.log("Response keys:", Object.keys(createdSupplier || {}));

      // Handle different possible response structures
      let supplierData = createdSupplier;

      // Check if response is wrapped in a data property
      if (createdSupplier?.data) {
        supplierData = createdSupplier.data;
        console.log("Found data wrapper, using supplierData:", supplierData);
      }

      // Check if response has a different structure
      if (!supplierData?._id && !supplierData?.id) {
        console.log("No _id or id found, checking for other ID fields...");
        console.log("Available fields:", Object.keys(supplierData || {}));
      }

      const supplierId = supplierData?._id || supplierData?.id;
      const supplierName = supplierData?.name;
      const supplierEmail = supplierData?.email;

      console.log("Extracted supplier ID:", supplierId);
      console.log("Extracted supplier name:", supplierName);
      console.log("Extracted supplier email:", supplierEmail);

      if (!supplierId) {
        console.error("No supplier ID found in response!");
        toast.error("Erreur: ID du fournisseur non trouvé");
        return;
      }

      if (!supplierName) {
        console.error("No supplier name found in response!");
        toast.error("Erreur: Nom du fournisseur non trouvé");
        return;
      }

      // Create a proper supplier object
      const properSupplier = {
        _id: supplierId,
        id: supplierId,
        name: supplierName,
        email: supplierEmail,
      };

      console.log("Proper supplier object:", properSupplier);

      // Update the form with the new supplier
      setFoundSupplier(properSupplier);
      setValue("supplierId", supplierId);

      // Also update the supplier input to show the supplier name
      setSupplierInput(supplierName);
      if (supplierEmail) {
        setSupplierEmail(supplierEmail);
      }

      // Trigger form validation to ensure the field is updated
      await trigger("supplierId");

      setShowCreateSupplier(false);
      setShowSupplierModal(false);

      // Force a re-render by updating a state
      setForceUpdate((prev) => prev + 1);

      console.log("=== IMMEDIATELY AFTER STATE UPDATES ===");
      console.log("Found supplier set to:", properSupplier);
      console.log("Form supplierId value:", watch("supplierId"));

      // Wait a bit to ensure state is updated
      setTimeout(() => {
        console.log("=== AFTER SUPPLIER CREATION (DELAYED) ===");
        console.log("Found supplier set to:", foundSupplier);
        console.log("Form supplierId value:", watch("supplierId"));
        console.log("Supplier input:", supplierInput);
        console.log("Supplier email:", supplierEmail);
        console.log("Show create supplier:", showCreateSupplier);
        console.log("Show supplier modal:", showSupplierModal);
      }, 200);

      toast.success(`Fournisseur créé et lié: ${supplierName}`);
    } catch (error) {
      console.error("Error linking created supplier:", error);
      toast.error("Erreur lors de la liaison du fournisseur");
    } finally {
      setIsCreatingSupplier(false);
    }
  };

  const handleCloseSupplierModal = () => {
    setShowSupplierModal(false);
    setSupplierToCreate(null);
  };

  const totals = useMemo(() => {
    const currentProducts = watchedProducts || [];

    if (!Array.isArray(currentProducts) || currentProducts.length === 0) {
      return { totalHT: 0, totalTVA: 0, totalTTC: 0 };
    }

    const calculated = currentProducts.reduce(
      (acc, p) => {
        const quantity = Number(p?.quantity) || 0;
        const priceHT = Number(p?.priceHT) || 0;
        const tva = Number(p?.tva) || 0;
        const remise = Number(p?.remise) || 0;

        const lineHT = quantity * priceHT - remise;
        const lineTVA = (lineHT * tva) / 100;
        const lineTotal = lineHT + lineTVA;

        acc.totalHT += lineHT;
        acc.totalTVA += lineTVA;
        acc.totalTTC += lineTotal;

        return acc;
      },
      { totalHT: 0, totalTVA: 0, totalTTC: 0 }
    );

    return calculated;
  }, [watchedProducts, totalsUpdateTrigger]);

  // Handle invoice input type change
  const handleInvoiceInputTypeChange = (e) => {
    const inputType = e.target.value;
    setInvoiceInputType(inputType);

    // Reset states when changing input type
    setImportedPdfFile(null);
    setPdfPreviewUrl(null);
    setExtractedData(null);
    setImportedImageFile(null);
    setValue("invoiceNumber", "");
  };

  // Handle image upload and processing
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner un fichier image valide");
      return;
    }

    setImportedImageFile(file);

    // Create image preview URL
    const imageUrl = URL.createObjectURL(file);
    setImagePreviewUrl(imageUrl);

    try {
      // Start AI processing
      setIsProcessingImage(true);
      setProcessingProgress(0);

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProcessingProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Process image with AI
      const extracted = await processImage(file);

      // Clear progress interval
      clearInterval(progressInterval);
      setProcessingProgress(100);

      // Set extracted data
      setExtractedData(extracted);

      // Auto-fill form with extracted data
      if (extracted.invoiceNumber) {
        setValue("invoiceNumber", extracted.invoiceNumber);
      }

      if (extracted.date) {
        setValue("date", extracted.date);
      }

      // Handle supplier
      if (extracted.supplier) {
        setSupplierInput(extracted.supplier);
        if (extracted.supplierEmail) {
          setSupplierEmail(extracted.supplierEmail);
          // Check if supplier exists
          const existingSupplier = suppliers?.find(
            (s) =>
              s.email?.toLowerCase() === extracted.supplierEmail.toLowerCase()
          );
          if (existingSupplier) {
            setFoundSupplier(existingSupplier);
            setValue("supplierId", existingSupplier.id);
            setShowCreateSupplier(false);
          } else {
            setFoundSupplier(null);
            setShowCreateSupplier(true);
          }
        }
      }

      // Add products to the form
      if (extracted.products && extracted.products.length > 0) {
        // Clear existing products
        replace([]);

        // Add extracted products
        extracted.products.forEach((product, index) => {
          append({
            name: product.name || "",
            description: product.description || "",
            quantity: product.quantity || 1,
            priceHT: product.priceHT || 0,
            tva: product.tva || 20,
            remise: product.remise || 0,
          });
        });

        // Trigger form validation
        trigger("products");
      }

      // Update description with supplier info
      if (extracted.supplier) {
        const currentDescription = watch("description") || "";
        const supplierInfo = `Fournisseur: ${extracted.supplier}`;
        setValue(
          "description",
          currentDescription
            ? `${currentDescription}\n${supplierInfo}`
            : supplierInfo
        );
      }

      toast.success(
        "Image traitée avec succès ! Données extraites et ajoutées au formulaire."
      );
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error(`Erreur lors du traitement de l'image: ${error.message}`);
    } finally {
      setIsProcessingImage(false);
      setProcessingProgress(0);
    }
  };

  // Handle PDF file upload and processing
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error(t("depense.form.error_pdf_only"));
      return;
    }

    try {
      setImportedPdfFile(file);

      // Show progress bar
      setIsProcessingPdf(true);
      setProcessingProgress(0);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPdfPreviewUrl(previewUrl);
      setProcessingProgress(20);

      // Process PDF and extract data
      setProcessingProgress(40);
      const extracted = await processPDFWithAI(file);
      setProcessingProgress(60);
      setExtractedData(extracted);

      // Auto-fill form with extracted data
      setProcessingProgress(70);
      if (extracted.invoiceNumber) {
        setValue("invoiceNumber", extracted.invoiceNumber);
      }

      // Format and set date
      if (extracted.date) {
        // Convert date format from DD/MM/YYYY to YYYY-MM-DD for input[type="date"]
        const dateParts = extracted.date.split(/[\/\-\.]/);
        if (dateParts.length === 3) {
          let day, month, year;
          if (dateParts[2].length === 4) {
            // Format: DD/MM/YYYY or DD-MM-YYYY
            [day, month, year] = dateParts;
          } else {
            // Format: YYYY/MM/DD or YYYY-MM-DD
            [year, month, day] = dateParts;
          }
          const formattedDate = `${year}-${month.padStart(
            2,
            "0"
          )}-${day.padStart(2, "0")}`;
          setValue("date", formattedDate);
        } else {
          setValue("date", extracted.date);
        }
      }

      if (extracted.supplier) {
        // Try to find matching supplier
        const matchingSupplier = suppliers?.data?.find(
          (s) =>
            s.name.toLowerCase().includes(extracted.supplier.toLowerCase()) ||
            extracted.supplier.toLowerCase().includes(s.name.toLowerCase())
        );
        if (matchingSupplier) {
          setValue("supplierId", matchingSupplier._id);
          setSupplierInput(matchingSupplier.name);
          setFoundSupplier(matchingSupplier);
        } else if (extracted.supplierEmail) {
          // Try to match by email
          const emailSupplier = suppliers?.data?.find(
            (s) =>
              s.email?.toLowerCase() === extracted.supplierEmail.toLowerCase()
          );
          if (emailSupplier) {
            setValue("supplierId", emailSupplier._id);
            setSupplierInput(emailSupplier.name);
            setFoundSupplier(emailSupplier);
          } else {
            // Set input for manual creation
            setSupplierInput(extracted.supplier);
            setSupplierEmail(extracted.supplierEmail);
            setShowCreateSupplier(true);
          }
        }
      }

      // Update products if extracted
      setProcessingProgress(80);
      if (extracted.products && extracted.products.length > 0) {
        // Clear existing products and add extracted ones
        replace(extracted.products);

        // Also set individual values to ensure form inputs are updated
        extracted.products.forEach((product, index) => {
          setValue(`products.${index}.name`, product.name || "");
          setValue(`products.${index}.quantity`, product.quantity || 1);
          setValue(`products.${index}.priceHT`, product.priceHT || 0);
          setValue(`products.${index}.tva`, product.tva || 20);
          setValue(`products.${index}.description`, product.description || "");
          setValue(`products.${index}.remise`, product.remise || 0);
        });

        // Force form to trigger re-render
        trigger("products");

        // Force component re-render
        setForceUpdate((prev) => prev + 1);

        // Force form to update
        setTimeout(() => {
          // Form values updated
        }, 100);

        // Show success message with product count
        toast.success(
          `${extracted.products.length} produit(s) extrait(s) et ajouté(s) au tableau "Produits achetés"`
        );

        // If there are more than 1 product, show a message to add more
        if (extracted.products.length > 1) {
          toast.info(
            `💡 Cliquez sur "Ajouter un produit" pour créer plus de lignes si nécessaire`
          );
        }
      }

      // Update description with minimal supplier info only
      setProcessingProgress(90);
      if (
        extracted.supplierAddress ||
        extracted.supplierEmail ||
        extracted.supplierPhone
      ) {
        let description = "";
        if (extracted.supplierAddress)
          description += `Adresse: ${extracted.supplierAddress}`;
        if (extracted.supplierEmail)
          description += description
            ? ` | Email: ${extracted.supplierEmail}`
            : `Email: ${extracted.supplierEmail}`;
        if (extracted.supplierPhone)
          description += description
            ? ` | Tel: ${extracted.supplierPhone}`
            : `Tel: ${extracted.supplierPhone}`;
        setValue("description", description);
      }

      // Complete progress bar
      setProcessingProgress(100);
      setTimeout(() => {
        setIsProcessingPdf(false);
        setProcessingProgress(0);
      }, 500);

      toast.success(t("depense.form.pdf_processed_success"));
    } catch (error) {
      console.error("Error processing PDF:", error);
      setIsProcessingPdf(false);
      setProcessingProgress(0);
      toast.error(t("depense.form.error_processing_pdf"));
    }
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      let finalSupplierId = "";
      let finalDescription = data.description || "";

      // Handle supplier logic - two scenarios
      let supplierInfo = null;

      console.log("=== SUPPLIER LOGIC DEBUG ===");
      console.log("foundSupplier exists:", !!foundSupplier);
      console.log("foundSupplier value:", foundSupplier);
      console.log("supplierInput value:", supplierInput);
      console.log("supplierInput.trim():", supplierInput?.trim());

      if (foundSupplier) {
        // Scenario 1: Existing supplier found by email - use supplier ID
        finalSupplierId = foundSupplier._id || foundSupplier.id;
        console.log(
          "✅ Using existing supplier:",
          foundSupplier.name,
          "ID:",
          finalSupplierId
        );
      } else if (supplierInput?.trim() !== "") {
        // Scenario 2: Supplier doesn't exist - save supplier info as JSON
        console.log("Saving supplier info as JSON:", supplierInput?.trim());
        supplierInfo = {
          name: supplierInput?.trim() || "",
          email: supplierEmail || null,
          address: null,
          phone: null,
        };
      } else {
        // No supplier information provided
        toast.error("Veuillez saisir un nom de fournisseur");
        return;
      }

      // Only append supplierId if we have a valid ID
      if (finalSupplierId) {
        formData.append("supplierId", finalSupplierId);
      }

      // Append supplierInfo as JSON if we have supplier info
      if (supplierInfo) {
        formData.append("supplierInfo", JSON.stringify(supplierInfo));
      }

      formData.append("description", finalDescription);

      formData.append("companyId", companyId);
      formData.append("createdBy", createdBy);
      formData.append("date", data.date);
      formData.append("invoiceNumber", data.invoiceNumber);

      // Debug: Log form data
      console.log("=== FORM SUBMISSION DEBUG ===");
      console.log("supplierId:", finalSupplierId);
      console.log("supplierInfo:", supplierInfo);
      console.log("foundSupplier:", foundSupplier);
      console.log("foundSupplier._id:", foundSupplier?._id);
      console.log("foundSupplier.id:", foundSupplier?.id);
      console.log("supplierInput:", supplierInput);
      console.log("supplierEmail:", supplierEmail);
      console.log("Form data supplierId:", data.supplierId);
      console.log("Form watch supplierId:", watch("supplierId"));
      console.log("Show create supplier:", showCreateSupplier);
      console.log("Is creating supplier:", isCreatingSupplier);

      // Log all form data entries
      console.log("FormData entries:");
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      data.products.forEach((product, index) => {
        formData.append(`products[${index}][name]`, product.name);
        formData.append(`products[${index}][quantity]`, product.quantity);
        formData.append(`products[${index}][priceHT]`, product.priceHT);
        formData.append(`products[${index}][tva]`, product.tva);
        formData.append(`products[${index}][description]`, product.description);
      });

      formData.append("totalHT", totals.totalHT.toString());
      formData.append("totalTVA", totals.totalTVA.toString());
      formData.append("totalTTC", totals.totalTTC.toString());

      if (data.justificatif && data.justificatif.length > 0) {
        formData.append("justificatif", data.justificatif[0]);
      }
      if (justificatifFile) {
        formData.append("justificatif", justificatifFile);
      }

      if (isUpdate && expense?._id) {
        await updateExpense({
          expenseId: expense?._id,
          companyId,
          body: formData,
        }).unwrap();
        toast.success(t("depense.toast_success_update"));
      } else {
        await createExpense(formData).unwrap();
        toast.success(t("depense.toast_success"));
        reset();
      }

      onClose?.();
    } catch (error) {
      toast.error(t("depense.toast_error"));
      console.error("Error submitting expense:", error);
    }
  };

  return (
    <div className="expense-form-container">
      {/* Progress Bar Modal */}
      {(isProcessingPdf || isProcessingImage) && (
        <div className="progress-modal-overlay">
          <div className="progress-modal">
            <div className="progress-content">
              <div className="progress-icon">
                {isProcessingPdf ? "🤖" : "📷"}
              </div>
              <h3>
                {isProcessingPdf
                  ? "AI Processing PDF..."
                  : "AI Processing Image..."}
              </h3>
              <p>
                {isProcessingPdf
                  ? "Extracting data from your PDF invoice"
                  : "Extracting data from your image invoice"}
              </p>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${processingProgress}%` }}></div>
              </div>
              <div className="progress-text">{processingProgress}%</div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        {/* Left Column: Form */}
        <div
          className={pdfPreviewUrl || imagePreviewUrl ? "col-md-6" : "col-12"}>
          <div className="section">
            {/* Supplier, Date, Invoice Number */}
            <div className="form-row">
              <div className="form-group">
                <label>{t("depense.form.supplier")} :</label>
                <div className="supplier-input-container">
                  <input
                    type="text"
                    value={supplierInput || ""}
                    onChange={handleSupplierInputChange}
                    placeholder="Nom du fournisseur ou email (ex: john@example.com)"
                    className={`supplier-input ${
                      errors.supplierId
                        ? "error"
                        : supplierInput?.trim() !== ""
                        ? "valid"
                        : ""
                    }`}
                  />

                  {/* Found Supplier Display */}
                  {foundSupplier && (
                    <div className="found-supplier">
                      <span className="supplier-name">
                        ✓ {foundSupplier.name}
                      </span>
                      <span className="supplier-email">
                        ({foundSupplier.email})
                      </span>
                      <span className="supplier-status">
                        Fournisseur existant - sera lié à la dépense
                      </span>
                      <div className="supplier-debug">
                        <small>
                          ID: {foundSupplier._id || foundSupplier.id}
                        </small>
                      </div>
                    </div>
                  )}

                  {/* Supplier Not Found Status */}
                  {showCreateSupplier && supplierEmail && (
                    <div className="supplier-not-found-section">
                      <p className="supplier-not-found">
                        Fournisseur non trouvé avec l'email:{" "}
                        <strong>{supplierEmail}</strong>
                      </p>
                      <div className="supplier-options">
                        <div className="supplier-status">
                          📝 Les informations du fournisseur seront sauvegardées
                          en JSON
                        </div>
                        <button
                          type="button"
                          className="btn-create-supplier"
                          onClick={handleCreateSupplier}
                          disabled={isCreatingSupplier}>
                          {isCreatingSupplier
                            ? "Création..."
                            : "Créer le fournisseur"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Manual Supplier Input Status */}
                  {supplierInput?.trim() !== "" &&
                    !foundSupplier &&
                    !supplierEmail && (
                      <div className="manual-supplier-section">
                        <div className="supplier-status">
                          📝 Les informations du fournisseur seront sauvegardées
                          en JSON
                        </div>
                      </div>
                    )}
                </div>

                {/* Hidden input for form validation */}
                <input
                  type="hidden"
                  {...register("supplierId", {
                    required: false, // Make it not required since we handle validation in onSubmit
                    validate: (value) => {
                      // Always valid - we handle supplier logic in onSubmit
                      return true;
                    },
                  })}
                />

                {errors.supplierId && (
                  <p className="error-message">
                    Veuillez saisir un nom de fournisseur
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>{t("depense.form.date")} :</label>
                <input
                  type="date"
                  {...register("date", { required: true })}
                  className={errors.date ? "error" : ""}
                />
                {errors.date && (
                  <p className="error-message">
                    {t("depense.form.error_required")}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>{t("depense.form.invoice_number")} :</label>

                {/* Input Type Selector */}
                <div className="invoice-input-type-selector">
                  <button
                    type="button"
                    className={`input-type-btn ${
                      invoiceInputType === "text" ? "active" : ""
                    }`}
                    onClick={() => setInvoiceInputType("text")}>
                    📝 Texte
                  </button>
                  <button
                    type="button"
                    className={`input-type-btn ${
                      invoiceInputType === "pdf" ? "active" : ""
                    }`}
                    onClick={() => setInvoiceInputType("pdf")}>
                    📄 PDF
                  </button>
                  <button
                    type="button"
                    className={`input-type-btn ${
                      invoiceInputType === "image" ? "active" : ""
                    }`}
                    onClick={() => setInvoiceInputType("image")}>
                    🖼️ Image
                  </button>
                </div>

                {/* Input Field Based on Type */}
                {invoiceInputType === "text" ? (
                  <input
                    type="text"
                    {...register("invoiceNumber", { required: true })}
                    className={errors.invoiceNumber ? "error" : ""}
                    placeholder="Numéro de facture"
                  />
                ) : invoiceInputType === "pdf" ? (
                  <div className="pdf-upload-container">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfUpload}
                      className="pdf-upload-input"
                    />
                    <div className="pdf-upload-info">
                      {importedPdfFile ? (
                        <span className="pdf-file-name">
                          {importedPdfFile.name}
                        </span>
                      ) : (
                        <span className="pdf-upload-placeholder">
                          {t("depense.form.select_pdf_file")}
                        </span>
                      )}
                      <div className="ai-status">
                        {aiStatus.available ? (
                          <span className="ai-enabled">
                            🤖 AI-powered extraction enabled
                          </span>
                        ) : (
                          <span className="ai-disabled">
                            ⚠️ AI extraction not available - using regex
                            fallback
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="image-upload-container">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="image-upload-input"
                    />
                    <div className="image-upload-info">
                      {importedImageFile ? (
                        <span className="image-file-name">
                          {importedImageFile.name}
                        </span>
                      ) : (
                        <span className="image-upload-placeholder">
                          {t("depense.form.select_image_file")}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {errors.invoiceNumber && (
                  <p className="error-message">
                    {t("depense.form.error_required")}
                  </p>
                )}
              </div>
            </div>

            <div className="form-group full">
              <label>{t("depense.form.description")} :</label>
              <textarea
                {...register("description", { required: true })}
                className={errors.description ? "error" : ""}
              />
              {errors.description && (
                <p className="error-message">
                  {t("depense.form.error_required")}
                </p>
              )}
            </div>
          </div>

          <TableProducts
            key={`table-products-${forceUpdate}`}
            append={append}
            errors={errors}
            fields={fields}
            products={watchedProducts}
            register={register}
            remove={remove}
            trigger={trigger}
            triggerTotalsUpdate={triggerTotalsUpdate}
            compactView={!!pdfPreviewUrl} // true if PDF imported (col-6)
            isFromPdf={
              !!extractedData &&
              extractedData.products &&
              extractedData.products.length > 0
            }
          />
          <div className="totals-note">
            <small>💡 Les totaux se mettent à jour automatiquement</small>
          </div>
          <div className="totals">
            <div>
              {t("depense.total.ht")} : {totals.totalHT.toFixed(2)} €
            </div>
            <div>
              {t("depense.total.tva")} : {totals.totalTVA.toFixed(2)} €
            </div>
            <div>
              <strong>
                {t("depense.total.ttc")} : {totals.totalTTC.toFixed(2)} €
              </strong>
            </div>
          </div>

          <button className="btn-submit" onClick={handleSubmit(onSubmit)}>
            {isUpdate
              ? t("depense.form.update_submit")
              : t("depense.form.submit")}
          </button>
        </div>

        {/* PDF Preview Section */}
        {pdfPreviewUrl && (
          <div className="col-md-6 pdf-preview-section">
            <div className="pdf-preview-header">
              <h4>{t("depense.form.pdf_preview")}</h4>
              <button
                type="button"
                className="btn-close-pdf"
                onClick={() => {
                  setPdfPreviewUrl(null);
                  setImportedPdfFile(null);
                  setExtractedData(null);
                }}>
                ×
              </button>
            </div>
            <div className="pdf-preview-container">
              <Document
                file={pdfPreviewUrl}
                onLoadSuccess={({ numPages }) => {
                  // PDF loaded successfully
                }}
                onLoadError={(error) => {
                  console.error("Error loading PDF:", error);
                  toast.error(t("depense.form.error_loading_pdf"));
                }}>
                <Page pageNumber={1} scale={0.8} />
              </Document>
            </div>
          </div>
        )}

        {/* Image Preview Section */}
        {imagePreviewUrl && (
          <div className="col-md-6 image-preview-section">
            <div className="image-preview-header">
              <h4>{t("depense.form.image_preview")}</h4>
              <button
                type="button"
                className="btn-close-image"
                onClick={() => {
                  setImagePreviewUrl(null);
                  setImportedImageFile(null);
                  setExtractedData(null);
                }}>
                ×
              </button>
            </div>
            <div className="image-preview-container">
              <img
                src={imagePreviewUrl}
                alt="Invoice preview"
                className="image-preview"
              />
            </div>
          </div>
        )}
      </div>

      {/* Extracted Data Section - Below Form and PDF */}
      {extractedData && (
        <div className="row">
          <div className="col-12">
            <div className="extracted-data-section">
              <div className="extracted-data-header">
                <h4>{t("depense.form.extracted_data")}</h4>
                <div className="extracted-data-actions">
                  <button
                    type="button"
                    className="btn-clear-extracted"
                    onClick={() => setExtractedData(null)}>
                    Effacer les données extraites
                  </button>
                </div>
              </div>

              <div className="extracted-data-content">
                <div className="extracted-info-grid">
                  {/* Basic Info */}
                  <div className="extracted-info-group">
                    <h5>Informations de base</h5>
                    {extractedData.invoiceNumber && (
                      <p>
                        <strong>{t("depense.form.invoice_number")}:</strong>{" "}
                        {extractedData.invoiceNumber}
                      </p>
                    )}
                    {extractedData.date && (
                      <p>
                        <strong>{t("depense.form.date")}:</strong>{" "}
                        {extractedData.date}
                      </p>
                    )}
                    {extractedData.supplier && (
                      <p>
                        <strong>{t("depense.form.supplier")}:</strong>{" "}
                        {extractedData.supplier}
                      </p>
                    )}
                  </div>

                  {/* Supplier Details */}
                  <div className="extracted-info-group">
                    <h5>Détails du fournisseur</h5>
                    {extractedData.supplierAddress && (
                      <p>
                        <strong>Adresse:</strong>{" "}
                        {extractedData.supplierAddress}
                      </p>
                    )}
                    {extractedData.supplierEmail && (
                      <p>
                        <strong>Email:</strong> {extractedData.supplierEmail}
                      </p>
                    )}
                    {extractedData.supplierPhone && (
                      <p>
                        <strong>Téléphone:</strong>{" "}
                        {extractedData.supplierPhone}
                      </p>
                    )}
                  </div>

                  {/* Totals */}
                  <div className="extracted-info-group">
                    <h5>Totaux extraits</h5>
                    {extractedData.totalHT > 0 && (
                      <p>
                        <strong>{t("depense.total.ht")}:</strong>{" "}
                        {extractedData.totalHT.toFixed(2)} €
                      </p>
                    )}
                    {extractedData.tva > 0 && (
                      <p>
                        <strong>TVA:</strong> {extractedData.tva.toFixed(2)} €
                      </p>
                    )}
                    {extractedData.totalTTC > 0 && (
                      <p>
                        <strong>{t("depense.total.ttc")}:</strong>{" "}
                        {extractedData.totalTTC.toFixed(2)} €
                      </p>
                    )}
                  </div>
                </div>

                {/* Products */}
                {extractedData.products &&
                  extractedData.products.length > 0 && (
                    <div className="extracted-products-section">
                      <h5>
                        Produits extraits ({extractedData.products.length})
                      </h5>
                      <div className="extracted-products-grid">
                        {extractedData.products.map((product, index) => (
                          <div key={index} className="extracted-product-card">
                            <div className="product-header">
                              <div className="product-name">{product.name}</div>
                              <div className="product-total">
                                {(product.quantity * product.priceHT).toFixed(
                                  2
                                )}
                                €
                              </div>
                            </div>
                            {product.description && (
                              <div className="product-description">
                                {product.description}
                              </div>
                            )}
                            <div className="product-details">
                              <span className="product-quantity">
                                {product.quantity}x
                              </span>
                              <span className="product-price">
                                {product.priceHT.toFixed(2)}€
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {extractedData.products.length > 1 && (
                        <div className="add-more-products-hint">
                          <small>
                            💡 Les produits ont été ajoutés au tableau
                            ci-dessus. Vous pouvez les modifier ou en ajouter
                            d'autres.
                          </small>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Creation Modal */}
      {showSupplierModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Créer un nouveau fournisseur</h3>
              <button
                type="button"
                className="modal-close"
                onClick={handleCloseSupplierModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <CreateSupplierComponent
                onClose={handleCloseSupplierModal}
                onSupplierCreated={handleSupplierCreated}
                supplier={supplierToCreate}
                isUpdate={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateExpenseComponent;
